import React, { useEffect, useState } from 'react';
import {
  Checkbox,
  List,
  ListItem,
  ListItemText,
  FormControl,
  FormGroup,
  FormControlLabel,
  TextField,
} from '@material-ui/core';
import { IntlShape, injectIntl } from 'react-intl';
import { get, isEmpty, uniqueId } from 'lodash';
import { arrayTranslate, translate } from 'containers/services/translate';
import messages from 'containers/PreOpeningPage/messages';
import SahamAccordion from 'components/SahamAccordion';
import {
  FraudContainer,
  FraudFormWrapper,
} from 'containers/PreOpeningPage/styled-component';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import { BUTTON_COLORS } from 'constants/design';
import FraudMotifDialog from 'containers/PreOpeningPage/FraudMotifDialog';
import { Listings } from 'containers/AppContainer/typings';
import { LISTINGS } from 'containers/data-structure';
import { OTHER_MOTIF_ID } from 'containers/PreOpeningPage/constants';
import { displayErrorText } from 'containers/services/validateForm';

export interface FraudeFormProps {
  intl: IntlShape;
  victims?: any;
  vehicles?: any;
  isManager?: boolean;
  lossDate?: string;
  fraudInfo: any;
  setFraudInfo: Function;
  updateFilde?: Function;
  claimToPreopen?: any;
  listings?: Listings;
  isRecapPage?: boolean;
  setClaimRecap?: Function;
  claimRecap?: any;
  fraudInfoValidation?: any;
  isPreOpening?: boolean;
  isCourtManager?: boolean;
  fullWidth?: boolean;
  setCommentMotif: Function;
  commentMotif: any;
  setFraudMotifs: Function;
  fraudMotifs: any;
}

const FraudeForm: React.FC<FraudeFormProps> = (props): JSX.Element => {
  const FRAUD_INFO_FIELDS = {
    VICTIMS: 'victims',
    VEHICULES: 'vehicules',
    OPPOSING_LAWYER: 'opposingLawyer',
    COMMENT: 'comment',
    COMMENT_COURT_MANAGER: 'commentCourtManager',
    FRAUD_MOTIFS: 'fraudMotifs',
  };

  const translateArrayErrorMessages = arrayTranslate(props.intl.formatMessage);
  const { message } = displayErrorText(
    props.fraudInfoValidation,
    translateArrayErrorMessages(props.fraudInfoValidation, true),
  );

  const [isFraudMotifDrawerOpen, setIsFraudMotifDrawerOpen] = useState(false);

  const handleOpenFraudMotif = () => setIsFraudMotifDrawerOpen(true);

  const [expanded, setExpanded] = useState(true);

  const fraudVictimsIds = get(
    props.claimToPreopen,
    'claim.fraudInfo[0].fraudVictims',
    [],
  ).map((el: any) => el.victimId);
  const fraudVehiculesIds = get(
    props.claimToPreopen,
    'claim.fraudInfo[0].fraudVehicules',
    [],
  ).map((el: any) => el.vehiculeId);

  useEffect(() => {
    props.setCommentMotif(
      get(props.claimToPreopen, 'claim.fraudInfo[0].fraudInfoMotif', []).find(
        (el: any) => el.motifId === OTHER_MOTIF_ID,
      )?.comment,
    );
    props.setFraudMotifs(
      new Set([
        ...get(
          props.claimToPreopen,
          'claim.fraudInfo[0].fraudInfoMotif',
          [],
        ).map((el: any) => el.motifId),
      ]),
    );
    props.setFraudInfo({
      victims: get(props.claimToPreopen, 'victims', [])
        .filter((v: any) => fraudVictimsIds.includes(v.id))
        .map((v: any) => v.cin),
      vehicules: get(props.claimToPreopen, 'vehicules', [])
        .filter((v: any) => fraudVehiculesIds.includes(v.id))
        .map((v: any) => v.registrationNumber),
      opposingLawyer: get(
        props.claimToPreopen,
        'claim.fraudInfo[0].opposingLawyer',
        '',
      ),
      fraudMotifs: get(
        props.claimToPreopen,
        'claim.fraudInfo[0].fraudMotifs',
        [],
      ),
      comment: get(props.claimToPreopen, 'claim.fraudInfo[0].comment', ''),
      decision: get(
        props.claimToPreopen,
        'claim.fraudInfo[0].fraudDecision[0]',
      ),
      commentMotif: get(
        props.claimToPreopen,
        'claim.fraudInfo[0].fraudInfoMotif',
        [],
      ).find((el: any) => el.motifId === OTHER_MOTIF_ID)?.comment,
    });
  }, [!props.claimToPreopen, props.isRecapPage]);

  const handleChange = (field: string, param: any = null) => (
    event: React.ChangeEvent<{
      name?: string;
      value?: any;
    }>,
  ) => {
    const { value } = event.target;
    switch (field) {
      case FRAUD_INFO_FIELDS.OPPOSING_LAWYER:
        props.setFraudInfo({ ...props.fraudInfo, opposingLawyer: value });

        break;
      case FRAUD_INFO_FIELDS.COMMENT:
        props.setFraudInfo({ ...props.fraudInfo, comment: value });

        break;
      case FRAUD_INFO_FIELDS.VICTIMS:
        if (get(props.fraudInfo, 'victims', []).includes(param)) {
          props.setFraudInfo({
            ...props.fraudInfo,
            victims: get(props.fraudInfo, 'victims', []).filter(
              (item: any) => item !== param,
            ),
          });
        } else {
          props.setFraudInfo({
            ...props.fraudInfo,
            victims: [...get(props.fraudInfo, 'victims', []), param],
          });
        }
        break;
      case FRAUD_INFO_FIELDS.VEHICULES:
        if (get(props.fraudInfo, 'vehicules', []).includes(param)) {
          props.setFraudInfo({
            ...props.fraudInfo,
            vehicules: get(props.fraudInfo, 'vehicules', []).filter(
              (item: any) => item !== param,
            ),
          });
        } else {
          props.setFraudInfo({
            ...props.fraudInfo,
            vehicules: [...get(props.fraudInfo, 'vehicules', []), param],
          });
        }
        break;
      case FRAUD_INFO_FIELDS.COMMENT_COURT_MANAGER:
        props.setFraudInfo({
          ...props.fraudInfo,
          decision: {
            ...get(props.fraudInfo, 'decision'),
            comment: value,
            status: 'PENDING',
          },
        });

        break;
      default:
        return props.claimToPreopen;
    }
  };

  const resolveFraudMotifs = (fraudMotifs: any) => {
    const fraudMotifsArr = [...fraudMotifs];
    if (fraudMotifsArr.includes(OTHER_MOTIF_ID)) {
      fraudMotifsArr.splice(fraudMotifsArr.indexOf(OTHER_MOTIF_ID), 1);
      fraudMotifsArr.push(OTHER_MOTIF_ID);
    }
    return fraudMotifsArr;
  };

  const handleChangeExpanded = (
    event: React.ChangeEvent<{}>,
    expanded: boolean,
  ) => {
    event.stopPropagation();
    setExpanded(expanded);
  };

  return (
    <FraudContainer>
      <SahamAccordion
        expanded={expanded}
        onChange={handleChangeExpanded}
        title={translate(messages.fraudFormTitle, props)}
      >
        <FraudFormWrapper style={{ width: props.fullWidth ? '444px' : '50%' }}>
          <FormControl>
            <TextField
              label={translate(messages.opposingLawyer, props)}
              name={translate(messages.opposingLawyer, props)}
              value={get(props.fraudInfo, 'opposingLawyer', '')}
              onChange={handleChange(FRAUD_INFO_FIELDS.OPPOSING_LAWYER)}
              disabled={props.isRecapPage || props.isPreOpening}
              error={
                !!get(props.fraudInfoValidation, [
                  FRAUD_INFO_FIELDS.OPPOSING_LAWYER,
                ])
              }
              helperText={message[FRAUD_INFO_FIELDS.OPPOSING_LAWYER]}
            />
          </FormControl>
          <FormControl style={{ width: props.fullWidth ? '50%' : '100%' }}>
            <TextField
              label={translate(messages.lossDate, props)}
              name={translate(messages.lossDate, props)}
              value={props.lossDate}
              disabled
            />
          </FormControl>
          <FormControl>
            {!isEmpty(get(props, 'victims', [])) && (
              <span>{translate(messages.victims, props)}</span>
            )}
            <List>
              {get(props, 'victims', []).map((item: any) => (
                <ListItem key={item.registrationNumber}>
                  <FormGroup key={uniqueId()}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={get(props.fraudInfo, 'victims', []).includes(
                            item.cin,
                          )}
                          onChange={handleChange(
                            FRAUD_INFO_FIELDS.VICTIMS,
                            item.cin,
                          )}
                          color="primary"
                          disabled={props.isRecapPage || props.isPreOpening}
                        />
                      }
                      label={item.firstName + ' ' + item.lastName}
                    />
                  </FormGroup>
                </ListItem>
              ))}
            </List>
            {!!get(props.fraudInfoValidation, [FRAUD_INFO_FIELDS.VICTIMS]) && (
              <span style={{ color: 'red' }}>
                {translate(messages.fraudVictimsRequiredTitle, props)}
              </span>
            )}
          </FormControl>
          <FormControl>
            {!isEmpty(get(props, 'vehicles', [])) && (
              <span>{translate(messages.vehicules, props)}</span>
            )}
            <List>
              {get(props, 'vehicles', []).map((item: any) => (
                <ListItem key={item.id}>
                  <FormGroup key={uniqueId()}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={get(
                            props.fraudInfo,
                            'vehicules',
                            [],
                          ).includes(item.registrationNumber)}
                          color="primary"
                          onChange={handleChange(
                            FRAUD_INFO_FIELDS.VEHICULES,
                            item.registrationNumber,
                          )}
                          disabled={props.isRecapPage || props.isPreOpening}
                        />
                      }
                      label={
                        item.registrationNumber
                          ? item.registrationNumber
                          : null + ' - ' + item.firstName
                          ? item.firstName
                          : null + ' ' + item.lastName
                          ? item.lastName
                          : null
                      }
                    />
                  </FormGroup>
                  <ListItemText primary={item.label} />
                </ListItem>
              ))}
            </List>
          </FormControl>
          <FormControl>
            {get(props.fraudInfo, 'comment') && props.isManager && (
              <TextField
                label={translate(
                  messages.fraudCommentOpeningManagerTitle,
                  props,
                )}
                name={translate(messages.comment, props)}
                value={get(props.fraudInfo, 'comment', '')}
                onChange={handleChange(FRAUD_INFO_FIELDS.COMMENT)}
              />
            )}
          </FormControl>
          <FormControl>
            {[...resolveFraudMotifs(props.fraudMotifs)].map(
              (fraudMotifId: any, index: number) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      data-testid={`checkbox-${index}`}
                      checked={true}
                      color="primary"
                    />
                  }
                  label={get(
                    get(props.listings, LISTINGS.FRAUD_MOTIF, []).find(
                      (el: any) => el.id === fraudMotifId,
                    ),
                    'title',
                    '',
                  )}
                />
              ),
            )}
            {!!get(props.fraudInfoValidation, [
              FRAUD_INFO_FIELDS.FRAUD_MOTIFS,
            ]) && (
              <span style={{ color: 'red' }}>
                {translate(messages.fraudMotifRequiredTitle, props)}
              </span>
            )}
          </FormControl>
          {props.commentMotif &&
            [...props.fraudMotifs].includes(OTHER_MOTIF_ID) && (
              <TextField
                label={translate(messages.fraudOtherMotifTitle, props)}
                name={translate(messages.comment, props)}
                value={props.commentMotif}
                disabled
              />
            )}
          {!props.isRecapPage && !props.isPreOpening && (
            <PrimaryButton
              background={BUTTON_COLORS.PRIMARY_BLUE}
              border={BUTTON_COLORS.PRIMARY_BLUE}
              titlecolor={BUTTON_COLORS.BLUE_BUTTON_TITLE}
              title={translate(messages.fraudMotif, props)}
              onClick={handleOpenFraudMotif}
            />
          )}
          {(get(props.fraudInfo, 'decision.comment') ||
            (props.isCourtManager &&
              props.isRecapPage &&
              isEmpty(get(props.fraudInfo, 'decision')))) && (
            <TextField
              label={translate(messages.fraudCommentCourtManagerTitle, props)}
              name={translate(messages.fraudCommentCourtManagerTitle, props)}
              value={get(props.fraudInfo, 'decision.comment', '')}
              onChange={handleChange(FRAUD_INFO_FIELDS.COMMENT_COURT_MANAGER)}
              disabled={
                !isEmpty(
                  get(props.claimToPreopen, 'claim.fraudInfo[0].fraudDecision'),
                )
              }
            />
          )}
        </FraudFormWrapper>
      </SahamAccordion>
      <FraudMotifDialog
        listings={props.listings}
        fraudMotifs={props.fraudMotifs}
        setFraudMotifs={props.setFraudMotifs}
        isOpen={isFraudMotifDrawerOpen}
        setIsFraudMotifDrawerOpen={setIsFraudMotifDrawerOpen}
        updateFilde={props.updateFilde}
        claimToPreopen={props.claimToPreopen}
        setClaimRecap={props.setClaimRecap}
        claimRecap={props.claimRecap}
        commentMotif={props.commentMotif}
        setCommentMotif={props.setCommentMotif}
        fraudInfo={props.fraudInfo}
        setFraudInfo={props.setFraudInfo}
      />
    </FraudContainer>
  );
};

export default injectIntl(FraudeForm);
